import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';

import { JDSIconProps } from './generated';
import { forwardRef } from 'react';

const legacyColors = [
  'primary',
  'secondary',
  'sparkle',
  'grey-100',
  'grey-80',
  'grey-60',
  'success',
  'error',
  'warning'
] as const;
type legacyColorsType = (typeof legacyColors)[number];

const legacyKinds = ['icon-only', 'background-bold'] as const;
type legacyKindsType = (typeof legacyKinds)[number];
const legacySizes = ['m', 's', 'l', 'xl', 'xxl'] as const;
type legacySizesType = (typeof legacySizes)[number];

export interface IconProps
  extends Omit<JDSIconProps, 'size' | 'color' | 'kind' | 'onClick'> {
  size?: legacySizesType | JDSIconProps['size'];
  color?: legacyColorsType | JDSIconProps['color'];
  kind?: legacyKindsType | JDSIconProps['kind'];
  ic: string | JSX.Element;
  onClick?: (ev: React.MouseEvent<HTMLDivElement>) => void;
}

export const PROP_MAP: IPROP_MAP<IconProps> = (props) => {
  if (legacyKinds.includes(props.kind as legacyKindsType)) {
    console.warn('these values for the kind prop are deprecated', legacyKinds);
  }
  if (legacySizes.includes(props.size as legacySizesType)) {
    console.warn('these values for the size prop are deprecated', legacySizes);
  }
  return {
    ic: {
      properties: {
        ic: props.ic
      }
    },
    size: {
      defaultValue: 'medium',
      variations: {
        m: {
          size: 'medium'
        },
        l: {
          size: 'large'
        },
        xl: {
          size: 'xtra-large'
        },
        xxl: {
          size: 'xtra-xtra-large'
        },
        s: {
          size: 'small'
        }
      }
    },
    color: {
      variations: {
        primary: {
          color: 'primary_50'
        },
        secondary: {
          color: 'secondary_50'
        },
        sparkle: {
          color: 'sparkle_50'
        },
        'grey-100': {
          color: 'primary_grey_100'
        },
        'grey-80': {
          color: 'primary_grey_80'
        },
        'grey-60': {
          color: 'primary_grey_60'
        },
        success: {
          color: 'feedback_success'
        },
        error: {
          color: 'feedback_error'
        },
        warning: {
          color: 'feedback_warning'
        }
      }
    },
    kind: {
      defaultValue: 'icon-only',
      variations: {
        'icon-only': {
          kind: 'default'
        },
        background: {
          kind: 'background'
        },
        'background-bold': {
          kind: 'background_bold'
        }
      }
    }
  };
};

const feedbackIcons: Record<string, string> = {
  feedback_success: 'IcSuccessColored',
  feedback_error: 'IcErrorColored',
  feedback_warning: 'IcWarningColored'
};
export const formatIconName = (iconName: string) => {
  if (typeof iconName === 'string' && iconName.startsWith('Ic')) {
    const words = iconName.slice(2).match(/[A-Z][a-z]+/g);
    return words ? words.join(' ').toLowerCase() + ' icon' : iconName;
  } else if (typeof iconName === 'string' && iconName.startsWith('ic_')) {
    const words = iconName.slice(3).split('_');
    return words ? words.join(' ').toLowerCase() + ' icon' : iconName;
  }
  return iconName;
};

export const withLogic = (
  Component: React.ComponentType<IconProps>
): React.FC<IconProps> =>
  forwardRef<HTMLDivElement, IconProps>((userProps: IconProps, ref) => {
    const props = modifyProps(userProps, PROP_MAP) as IconProps;
    let iconComponent: string | JSX.Element = props.ic;
    if (typeof props.ic === 'string') {
      if (props.color && Object.keys(feedbackIcons).includes(props.color))
        iconComponent = feedbackIcons[props.color] || props.ic;
    }
    return (
      <Component
        {...{
          ...props,
          rootRef: ref,
          ariaLabel: props.ariaLabel || formatIconName(props.ic as string)
        }}
        ic={iconComponent}
      />
    );
  });
